import type { HttpPort } from "~/lib/http";

export type GoalSource = "teacher" | "student" | "system";

export interface GoalCategory {
  id: string;
  name: string;
  description?: string | null;
  icon?: string | null;
  color?: string | null;
}

export interface PoolGoal {
  id: string;
  title: string;
  description: string | null;
  category_id?: string | null;
  category_name?: string | null;
  category_color?: string | null;
  category_icon?: string | null;
  source: GoalSource;
  created_by_name?: string | null;
}

export interface GoalProposal {
  id: string;
  title: string;
  description: string | null;
  student_name: string;
  created_at: string;
}

export interface GoalVotingSession {
  id: string;
  status: "open" | "closed" | "results";
  week_start: string;
}

export interface GoalsSnapshot {
  goals: PoolGoal[];
  categories: GoalCategory[];
  proposals: GoalProposal[];
  votingSession: GoalVotingSession | null;
}

export interface LibraryGoal {
  title: string;
  description: string;
  category: string;
  icon: string;
  categoryId: string | null;
  categoryColor: string;
}

export interface GoalLibrary {
  groups: Record<string, LibraryGoal[]>;
  total: number;
}

export interface GoalDraft {
  title: string;
  description: string;
  categoryId: string | null;
}

export type GoalsCommand =
  | { type: "add"; draft: GoalDraft }
  | { type: "remove"; goalId: string }
  | { type: "approve-proposal"; proposalId: string }
  | { type: "reject-proposal"; proposalId: string }
  | { type: "add-library"; goals: LibraryGoal[] };

export function createGoalsModule(http: HttpPort, token: string) {
  return {
    async load(classId: string): Promise<GoalsSnapshot> {
      const [pool, voting, categories, proposals] = await Promise.all([
        http.request<{ goals?: PoolGoal[] }>({ path: `/goals/${classId}/pool`, token }),
        http.request<{ session?: GoalVotingSession | null }>({ path: `/voting/${classId}/current`, token }),
        http.request<{ categories?: GoalCategory[] }>({ path: "/goals/categories", token }),
        http.request<{ proposals?: GoalProposal[] }>({ path: `/goals/${classId}/proposals`, token })
      ]);
      return {
        goals: pool.goals ?? [],
        categories: categories.categories ?? [],
        proposals: proposals.proposals ?? [],
        votingSession: voting.session ?? null
      };
    },

    async loadLibrary(): Promise<GoalLibrary> {
      const value = await http.request<{ library?: Record<string, LibraryGoal[]>; total?: number }>({ path: "/goals/library", token });
      return { groups: value.library ?? {}, total: value.total ?? 0 };
    },

    async countPending(classId: string): Promise<number> {
      const value = await http.request<{ proposals?: GoalProposal[] }>({ path: `/goals/${classId}/proposals`, token });
      return value.proposals?.length ?? 0;
    },

    async execute(classId: string, command: GoalsCommand): Promise<{ message?: string; added?: number }> {
      if (command.type === "add") {
        const title = command.draft.title.trim();
        if (!title) throw new Error("Titel erforderlich");
        return http.request({
          path: `/goals/${classId}/pool`, method: "POST", token,
          body: { title, description: command.draft.description.trim(), categoryId: command.draft.categoryId || null }
        });
      }
      if (command.type === "remove") {
        return http.request({ path: `/goals/${classId}/pool/${command.goalId}`, method: "DELETE", token });
      }
      if (command.type === "approve-proposal") {
        return http.request({ path: `/goals/${classId}/proposals/${command.proposalId}/approve`, method: "PATCH", token, body: {} });
      }
      if (command.type === "reject-proposal") {
        return http.request({ path: `/goals/${classId}/proposals/${command.proposalId}/reject`, method: "PATCH", token, body: {} });
      }
      if (command.goals.length === 0) throw new Error("Bitte mindestens ein Ziel auswählen");
      return http.request({ path: `/goals/${classId}/pool/bulk`, method: "POST", token, body: { goals: command.goals } });
    }
  };
}

export type GoalsModule = ReturnType<typeof createGoalsModule>;

export function formatGoalDate(value: string): string {
  return new Date(value).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}
